import type { TFunction } from 'i18next';

import { isFreeSpinMiniGame } from '../../../../../features/widgets/utils/widget.utils';
import type { FreeSpinsPayload } from '../../../../../stores/rewards/types';
import type { Leaderboard, LeaderboardPrize, TournamentOfChampionsRound } from '../../../../../stores/tournament/types';
import { formatAmountByCoinType } from '../../../../../utils/prize';

const PRIZE_SLOTS_PER_ROUND = 4;

function miniGamePlaceholderSlot(roundIndex: number, prizeSlot: number): number {
  return roundIndex * PRIZE_SLOTS_PER_ROUND + prizeSlot + 1;
}

function resolvePlayerPrize(
  round: TournamentOfChampionsRound,
  leaderboard: Leaderboard | undefined,
): LeaderboardPrize | undefined {
  return round.playerPrize ?? leaderboard?.rankings?.find((r) => r.position === round.playerPosition)?.prize;
}

export interface ResolvedPrize {
  prizeType: 'CC' | 'SC' | 'FREE_SPINS' | 'MINI_GAME';
  coinsAmount?: string;
  placeholderNum?: number;
  imageUrl?: string;
}

function buildPrizes(
  round: TournamentOfChampionsRound,
  playerPrize: LeaderboardPrize | undefined,
  t: TFunction,
  locale?: string,
): ResolvedPrize[] {
  const source = playerPrize
    ? { gcCoin: playerPrize.gcCoin, scCoin: playerPrize.scCoin, bundleItems: playerPrize.bundleItems }
    : round.prizePool
      ? { gcCoin: round.prizePool.gcCoin, scCoin: round.prizePool.scCoin, bundleItems: undefined }
      : null;

  if (!source) return [];

  const prizes: ResolvedPrize[] = [];
  if (source.gcCoin > 0) {
    prizes.push({
      prizeType: 'CC',
      coinsAmount: formatAmountByCoinType({ amount: source.gcCoin, coinType: 'GC', t, locale }),
    });
  }
  if (source.scCoin > 0) {
    prizes.push({
      prizeType: 'SC',
      coinsAmount: formatAmountByCoinType({ amount: source.scCoin, coinType: 'SC', t, locale }),
    });
  }

  if (source.bundleItems) {
    for (const item of source.bundleItems) {
      if (item.bundleType !== 'freeSpins') continue;
      const payload = item.bundlePayload as FreeSpinsPayload;
      const gameId = payload.gameIds?.[0] ?? 0;

      if (isFreeSpinMiniGame(payload.provider)) {
        prizes.push({
          prizeType: 'MINI_GAME',
          coinsAmount: String(payload.rounds),
          placeholderNum: miniGamePlaceholderSlot(round.roundIndex, prizes.length),
          imageUrl: payload.gameImageUrl,
        });
      } else {
        prizes.push({
          prizeType: 'FREE_SPINS',
          coinsAmount: String(payload.rounds),
          placeholderNum: (gameId % 15) + 1,
        });
      }
    }
  }

  return prizes.slice(0, 4);
}

/**
 * The prizes shown for one v6 banner round: the player's prize once finalized, else the prize pool.
 * A finalized round the player won nothing in returns [] (the banner shows "No Rewards"), never the pool.
 */
export function resolveRoundPrizes(
  round: TournamentOfChampionsRound,
  leaderboard: Leaderboard | undefined,
  t: TFunction,
  locale?: string,
): ResolvedPrize[] {
  const playerPrize = round.isFinalized ? resolvePlayerPrize(round, leaderboard) : undefined;
  if (round.isFinalized && !playerPrize) return [];
  return buildPrizes(round, playerPrize, t, locale);
}
